import { createContainer } from 'shared-components/unstated'
import { useCallback, useMemo, useRef, useState } from 'react'
import type { ConversationOpenSourceName } from 'shared-code/app-events'

/**
 * Where the reader opened the conversation from — the analytics `source` of
 * `conversation-opened`. The one union lives in `shared-code/app-events.ts` (per-value notes there).
 */
export type ConversationOpenSource = ConversationOpenSourceName

/**
 * The id of the panel's `<h2>` — the sheet / drawer's `aria-labelledby`. Here, not in
 * `ConversationPanel`, so the host can name it without pulling the lazy panel chunk in.
 */
export const CONVERSATION_TITLE_ID = 'conversation-panel-title'

/** A header element that marks where the mobile bar docks while the header is slid back in:
 *  SoundGuys' top row — the bar then shows in its phone search row's place (H2, 2026-10-25).
 *  Without one, the bar docks under the whole header (Android Authority). Here, not in the bar,
 *  so a header can mark its row without pulling the bar in. */
export const CONVERSATION_DOCK_ATTR = 'data-conversation-dock'

export interface ConversationOpenOptions {
  /** Put the caret in the composer once the panel is open ("Add your take…"). */
  focusComposer?: boolean
}

export interface ConversationOpenRequest extends Required<ConversationOpenOptions> {
  source: ConversationOpenSource
  /** Monotonic — lets the host react to a repeat open from the same source. */
  seq: number
}

interface ConversationContextProps {
  /**
   * The commentHub container id of the article on this page, set by the one host
   * (`ArticleConversation`) while it is mounted. Null = no conversation on this page: entry
   * points fall back to their pre-panel behaviour (scroll to `#comments-section`) or render nothing.
   */
  containerId: string | null
  isOpen: boolean
  /** The latest open request; null once closed. The host reads `focusComposer` from it. */
  request: ConversationOpenRequest | null
  open: (source: ConversationOpenSource, options?: ConversationOpenOptions) => void
  close: () => void
  /** Host only. Returns the unregister function; call it on unmount. */
  registerHost: (containerId: string) => () => void
  /** The mobile top bar is on screen (below `lg`): the under-nav deal bar and best-buy panel
   *  step aside for it (`layout/UnderNavBlock.tsx`, Guillaume 2026-10-25). */
  barShown: boolean
  /** The bar only. */
  setBarShown: (shown: boolean) => void
}

/**
 * The one "is the conversation panel open, and from where" state of a page. Provided once per
 * app in `Layout`, above the templates, so the byline, the rail card, the mobile top bar, the
 * end-of-article block all open the same panel. Holds no comment data —
 * the panel's thread is the widget's, and the previews come from `useConversationPreview`.
 */
export const ConversationContext = createContainer<ConversationContextProps>(() => {
  const [containerId, setContainerId] = useState<string | null>(null)
  const [request, setRequest] = useState<ConversationOpenRequest | null>(null)
  const [barShown, setBarShown] = useState(false)
  const seq = useRef(0)

  const open = useCallback((source: ConversationOpenSource, options?: ConversationOpenOptions) => {
    seq.current += 1
    setRequest({ source, focusComposer: options?.focusComposer ?? false, seq: seq.current })
  }, [])

  const close = useCallback(() => setRequest(null), [])

  const registerHost = useCallback((id: string) => {
    setContainerId(id)
    return () => {
      setContainerId(current => (current === id ? null : current))
      setRequest(null)
      setBarShown(false)
    }
  }, [])

  return useMemo(
    () => ({
      containerId,
      isOpen: request !== null,
      request,
      open,
      close,
      registerHost,
      barShown,
      setBarShown,
    }),
    [containerId, request, open, close, registerHost, barShown]
  )
})
